// src/pages/Support.js

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  FiGlobe,
  FiChevronLeft,
  FiHeadphones,
  FiMail,
  FiMessageSquare,
  FiSend,
  FiCheck,
  FiAlertCircle,
  FiClock,
  FiUsers
} from 'react-icons/fi';
import SEO from '../components/SEO';
import { siteMeta } from '../seo/siteMeta';

// Styled components para la página de soporte
const PageContainer = styled.div`
  padding-top: 80px;
  min-height: 100vh;
  background: linear-gradient(135deg, #1A365D 0%, #0B1D3A 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(135deg, rgba(255,255,255,0.03) 25%, transparent 25%),
      linear-gradient(225deg, rgba(255,255,255,0.03) 25%, transparent 25%),
      linear-gradient(315deg, rgba(255,255,255,0.03) 25%, transparent 25%),
      linear-gradient(45deg, rgba(255,255,255,0.03) 25%, transparent 25%);
    background-size: 60px 60px;
    opacity: 0.4;
    z-index: 0;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem var(--spacing-md) 4rem;
  position: relative;
  z-index: 1;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-lg);
  width: 100%;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  cursor: pointer;
  background: none;
  border: none;
  text-decoration: none;
  background-color: rgba(0, 0, 0, 0.2);
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-sm);
  backdrop-filter: blur(8px);
  transition: all var(--transition-fast);
  border: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 10;

  svg {
    margin-right: var(--spacing-xs);
  }

  &:hover {
    color: var(--white);
    background-color: rgba(0, 0, 0, 0.3);
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
  }
`;

const LanguageToggle = styled.div`
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: var(--border-radius-md);
  padding: 0.5rem 0.75rem;
  box-shadow: var(--shadow-sm);
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all var(--transition-fast);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 5;

  &:hover {
    background-color: rgba(0, 0, 0, 0.3);
    box-shadow: var(--shadow-md);
  }
`;

const LanguageIcon = styled.div`
  margin-right: var(--spacing-xs);
  display: flex;
  align-items: center;
  color: var(--white);
`;

const LanguageText = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--white);
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: var(--spacing-xl);
  background-color: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: var(--spacing-xl) var(--spacing-md);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(135deg, rgba(255,255,255,0.05) 25%, transparent 25%),
      linear-gradient(225deg, rgba(255,255,255,0.05) 25%, transparent 25%),
      linear-gradient(315deg, rgba(255,255,255,0.05) 25%, transparent 25%),
      linear-gradient(45deg, rgba(255,255,255,0.05) 25%, transparent 25%);
    background-size: 60px 60px;
    opacity: 0.5;
    z-index: 0;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: var(--spacing-md);
  color: var(--white);
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeaderIcon = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: var(--spacing-md);

  svg {
    font-size: 3rem;
    color: var(--white);
    opacity: 0.9;
  }
`;

const PageSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.85);
  font-size: 1.1rem;
  line-height: 1.6;
  max-width: 700px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const SupportLayout = styled.div`
  display: flex;
  gap: var(--spacing-xl);

  @media (max-width: 992px) {
    flex-direction: column;
  }
`;

const SupportInfo = styled(motion.div)`
  flex: 1;
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(4px);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl);
  box-shadow: var(--shadow-md);
  border: 1px solid rgba(255, 255, 255, 0.3);

  @media (max-width: 992px) {
    order: 2;
  }
`;

const SupportInfoTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: var(--spacing-lg);
  color: var(--secondary);
  display: flex;
  align-items: center;

  svg {
    margin-right: var(--spacing-sm);
    color: var(--accent);
  }
`;

const InfoSection = styled.div`
  margin-bottom: var(--spacing-lg);

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoSectionTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: var(--spacing-md);
  color: var(--text-primary);
  display: flex;
  align-items: center;

  svg {
    margin-right: var(--spacing-xs);
    color: var(--accent);
  }
`;

const InfoText = styled.p`
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: var(--spacing-sm);

  a {
    color: var(--accent);
    text-decoration: none;
    font-weight: 500;
    transition: color var(--transition-fast);

    &:hover {
      color: var(--primary);
      text-decoration: underline;
    }
  }

  strong {
    color: var(--text-primary);
  }
`;

const ContactBox = styled.div`
  background-color: rgba(71, 181, 255, 0.08);
  border: 1px solid var(--accent);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-md);
  margin-top: var(--spacing-md);
`;

const ContactItem = styled.div`
  margin-bottom: ${props => props.isLast ? '0' : '0.75rem'};

  strong {
    display: block;
    color: var(--text-primary);
    font-size: 0.95rem;
    margin-bottom: 2px;
  }

  a {
    display: block;
    color: var(--accent);
    text-decoration: none;
    font-weight: 500;
    transition: color var(--transition-fast);
    font-size: 0.95rem;

    &:hover {
      color: var(--primary);
      text-decoration: underline;
    }
  }
`;

const SupportForm = styled(motion.div)`
  flex: 1.5;
  background-color: var(--white);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl);
  box-shadow: var(--shadow-lg);
  border: 1px solid rgba(255, 255, 255, 0.5);

  @media (max-width: 992px) {
    order: 1;
  }
`;

const FormTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: var(--spacing-lg);
  color: var(--secondary);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const FormRow = styled.div`
  display: flex;
  gap: var(--spacing-md);

  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const FormField = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: var(--spacing-xs);
  color: var(--text-primary);
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid var(--border);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  transition: all var(--transition-fast);
  background-color: var(--white);

  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(71, 181, 255, 0.1);
  }

  ${props => props.error && `
    border-color: var(--error);

    &:focus {
      box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
    }
  `}
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid var(--border);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  transition: all var(--transition-fast);
  background-color: var(--white);
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(71, 181, 255, 0.1);
  }

  ${props => props.error && `
    border-color: var(--error);

    &:focus {
      box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
    }
  `}
`;

const TextArea = styled.textarea`
  padding: 0.75rem 1rem;
  border: 1px solid var(--border);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  min-height: 160px;
  resize: vertical;
  font-family: var(--font-main);
  transition: all var(--transition-fast);
  background-color: var(--white);

  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(71, 181, 255, 0.1);
  }

  ${props => props.error && `
    border-color: var(--error);

    &:focus {
      box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
    }
  `}
`;

const ErrorMessage = styled.div`
  color: var(--error);
  font-size: 0.85rem;
  margin-top: 4px;
  display: flex;
  align-items: center;

  svg {
    margin-right: 4px;
    font-size: 0.9rem;
  }
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.875rem 2rem;
  background-color: var(--primary);
  color: var(--white);
  border: none;
  border-radius: var(--border-radius-md);
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all var(--transition-fast);
  margin-top: var(--spacing-sm);

  svg {
    margin-left: var(--spacing-sm);
  }

  &:hover:not(:disabled) {
    background-color: var(--primary-light);
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }

  &:disabled {
    background-color: var(--text-light);
    cursor: not-allowed;
    transform: none;
  }
`;

const FormMessage = styled.div`
  padding: var(--spacing-md);
  border-radius: var(--border-radius-md);
  margin-top: var(--spacing-md);
  display: flex;
  align-items: center;
  animation: slideDown 0.3s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  svg {
    margin-right: var(--spacing-sm);
    font-size: 1.25rem;
    flex-shrink: 0;
  }

  ${props => props.type === 'success' && `
    background-color: rgba(40, 167, 69, 0.1);
    color: var(--success);
    border: 1px solid rgba(40, 167, 69, 0.3);
  `}

  ${props => props.type === 'error' && `
    background-color: rgba(220, 53, 69, 0.1);
    color: var(--error);
    border: 1px solid rgba(220, 53, 69, 0.3);
  `}
`;

const ProductBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  background-color: ${props => props.product === 'volpetrack'
    ? 'rgba(71, 181, 255, 0.15)'
    : 'rgba(232, 120, 48, 0.15)'};
  color: ${props => props.product === 'volpetrack'
    ? 'var(--accent)'
    : '#E87830'};
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-left: var(--spacing-sm);
`;

// Animation variants
const slideInLeft = {
  hidden: { opacity: 0, x: -30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6 }
  }
};

const slideInRight = {
  hidden: { opacity: 0, x: 30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6 }
  }
};

const Support = () => {
  const seo = siteMeta.routeSeo.support;
  const [language, setLanguage] = useState('en');
  const [formState, setFormState] = useState({
    product: 'volpetrack',
    name: '',
    email: '',
    subject: '',
    issueType: 'general',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formMessage, setFormMessage] = useState(null);

  const [infoRef, infoInView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const [formRef, formInView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const toggleLanguage = () => setLanguage(language === 'en' ? 'es' : 'en');
  const handleBackClick = () => { window.location.href = '/'; };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formState.name.trim()) {
      newErrors.name = language === 'en' ? 'Name is required' : 'El nombre es requerido';
    }

    if (!formState.email.trim()) {
      newErrors.email = language === 'en' ? 'Email is required' : 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email)) {
      newErrors.email = language === 'en' ? 'Please enter a valid email' : 'Ingresa un email válido';
    }

    if (!formState.subject.trim()) {
      newErrors.subject = language === 'en' ? 'Subject is required' : 'El asunto es requerido';
    }

    if (!formState.message.trim()) {
      newErrors.message = language === 'en' ? 'Message is required' : 'El mensaje es requerido';
    } else if (formState.message.trim().length < 10) {
      newErrors.message = language === 'en'
        ? 'Please provide more details (minimum 10 characters)'
        : 'Proporciona más detalles (mínimo 10 caracteres)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);

      try {
        // Enviar solicitud de soporte al backend
        const response = await fetch('https://api.xadevs.com/api/support/send', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            product: formState.product,
            name: formState.name,
            email: formState.email,
            subject: formState.subject,
            issueType: formState.issueType,
            message: formState.message,
            language: language,
            device: getDeviceType(),
            browser: getBrowserName(),
            os: getOSName(),
            country: '' // Podemos agregar detección de país si es necesario
          })
        });

        const data = await response.json();

        if (response.ok && data.success) {
          setFormMessage({
            type: 'success',
            text: language === 'en'
              ? 'Thank you! Your support request has been received. We will get back to you within 24-48 hours.'
              : 'Gracias! Tu solicitud de soporte ha sido recibida. Te responderemos en 24-48 horas.'
          });

          // Reset form
          setFormState({
            product: formState.product,
            name: '',
            email: '',
            subject: '',
            issueType: 'general',
            message: ''
          });

          // Clear success message after 8 seconds
          setTimeout(() => {
            setFormMessage(null);
          }, 8000);
        } else {
          throw new Error(data.message || 'Failed to send support request');
        }
      } catch (error) {
        console.error('Support request error:', error);
        setFormMessage({
          type: 'error',
          text: language === 'en'
            ? 'Failed to send support request. Please try again later or email us directly at support@xadevs.com'
            : 'Error al enviar solicitud de soporte. Por favor intenta más tarde o escríbenos a support@xadevs.com'
        });

        setTimeout(() => {
          setFormMessage(null);
        }, 8000);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setFormMessage({
        type: 'error',
        text: language === 'en'
          ? 'Please fix the errors above and try again.'
          : 'Por favor corrige los errores arriba e intenta de nuevo.'
      });

      setTimeout(() => {
        setFormMessage(null);
      }, 5000);
    }
  };

  // Funciones auxiliares para detectar info del dispositivo
  const getDeviceType = () => {
    const userAgent = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(userAgent)) {
      return 'Tablet';
    } else if (/Mobile|iPhone|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
      return 'Mobile';
    }
    return 'Desktop';
  };

  const getBrowserName = () => {
    const userAgent = navigator.userAgent;
    if (userAgent.indexOf('Chrome') > -1) return 'Chrome';
    if (userAgent.indexOf('Safari') > -1) return 'Safari';
    if (userAgent.indexOf('Firefox') > -1) return 'Firefox';
    if (userAgent.indexOf('MSIE') > -1 || userAgent.indexOf('Trident/') > -1) return 'IE';
    if (userAgent.indexOf('Edge') > -1) return 'Edge';
    return 'Unknown';
  };

  const getOSName = () => {
    const userAgent = navigator.userAgent;
    if (userAgent.indexOf('Windows') > -1) return 'Windows';
    if (userAgent.indexOf('Mac') > -1) return 'MacOS';
    if (userAgent.indexOf('Linux') > -1) return 'Linux';
    if (userAgent.indexOf('Android') > -1) return 'Android';
    if (userAgent.indexOf('iOS') > -1 || userAgent.indexOf('iPhone') > -1 || userAgent.indexOf('iPad') > -1) return 'iOS';
    return 'Unknown';
  };

  const content = {
    en: {
      title: 'Support Center',
      subtitle: 'We\'re here to help! Select your product and let us know how we can assist you.',
      infoTitle: 'Support Information',
      responseTitle: 'Response Time',
      responseText: 'We typically respond within 24-48 hours during business days. For urgent matters, please specify in your message.',
      contactTitle: 'Direct Contact',
      emailSupport: 'General Support',
      emailPrivacy: 'Privacy & Data',
      emailDeveloper: 'Developer Contact',
      productsTitle: 'Our Products',
      volpetrackDesc: 'AI-powered financial assistant for smart money management',
      holdthenDesc: 'B2B international logistics platform for package forwarding and consolidation',
      formTitle: 'Submit a Support Request',
      productLabel: 'Product',
      nameLabel: 'Full Name',
      emailLabel: 'Email Address',
      issueTypeLabel: 'Issue Type',
      subjectLabel: 'Subject',
      messageLabel: 'Describe Your Issue',
      submitButton: 'Send Request',
      submitting: 'Sending...',
      issueTypes: {
        general: 'General Inquiry',
        technical: 'Technical Issue',
        account: 'Account & Billing',
        feature: 'Feature Request',
        bug: 'Bug Report',
        other: 'Other'
      }
    },
    es: {
      title: 'Centro de Soporte',
      subtitle: 'Estamos aquí para ayudarte! Selecciona tu producto y déjanos saber cómo podemos asistirte.',
      infoTitle: 'Información de Soporte',
      responseTitle: 'Tiempo de Respuesta',
      responseText: 'Normalmente respondemos dentro de 24-48 horas en días hábiles. Para asuntos urgentes, especifícalo en tu mensaje.',
      contactTitle: 'Contacto Directo',
      emailSupport: 'Soporte General',
      emailPrivacy: 'Privacidad y Datos',
      emailDeveloper: 'Contacto Desarrollador',
      productsTitle: 'Nuestros Productos',
      volpetrackDesc: 'Asistente financiero con IA para gestión inteligente de dinero',
      holdthenDesc: 'Plataforma B2B de logística internacional para casillero y consolidación',
      formTitle: 'Enviar Solicitud de Soporte',
      productLabel: 'Producto',
      nameLabel: 'Nombre Completo',
      emailLabel: 'Correo Electrónico',
      issueTypeLabel: 'Tipo de Consulta',
      subjectLabel: 'Asunto',
      messageLabel: 'Describe tu Problema',
      submitButton: 'Enviar Solicitud',
      submitting: 'Enviando...',
      issueTypes: {
        general: 'Consulta General',
        technical: 'Problema Técnico',
        account: 'Cuenta y Facturación',
        feature: 'Solicitud de Función',
        bug: 'Reporte de Error',
        other: 'Otro'
      }
    }
  };

  const t = content[language];

  return (
    <>
      <SEO title={seo.title} description={seo.description} path={seo.path} />
      <PageContainer>
        <ContentWrapper>
        <TopBar>
          <BackButton onClick={handleBackClick}>
            <FiChevronLeft /> {language === 'en' ? 'Back to Home' : 'Volver al Inicio'}
          </BackButton>
          <LanguageToggle onClick={toggleLanguage}>
            <LanguageIcon><FiGlobe /></LanguageIcon>
            <LanguageText>{language === 'en' ? 'Español' : 'English'}</LanguageText>
          </LanguageToggle>
        </TopBar>

        <PageHeader>
          <HeaderIcon>
            <FiHeadphones />
          </HeaderIcon>
          <PageTitle>{t.title}</PageTitle>
          <PageSubtitle>{t.subtitle}</PageSubtitle>
        </PageHeader>

        <SupportLayout>
          <SupportInfo
            ref={infoRef}
            initial="hidden"
            animate={infoInView ? "visible" : "hidden"}
            variants={slideInLeft}
          >
            <SupportInfoTitle>
              <FiMessageSquare />
              {t.infoTitle}
            </SupportInfoTitle>

            <InfoSection>
              <InfoSectionTitle>
                <FiClock />
                {t.responseTitle}
              </InfoSectionTitle>
              <InfoText>
                {t.responseText}
              </InfoText>
            </InfoSection>

            <InfoSection>
              <InfoSectionTitle>
                <FiMail />
                {t.contactTitle}
              </InfoSectionTitle>
              <ContactBox>
                <ContactItem>
                  <strong>{t.emailSupport}</strong>
                  <a href="mailto:support@xadevs.com">support@xadevs.com</a>
                </ContactItem>
                <ContactItem>
                  <strong>{t.emailPrivacy}</strong>
                  <a href="mailto:privacy@xadevs.com">privacy@xadevs.com</a>
                </ContactItem>
                <ContactItem isLast>
                  <strong>{t.emailDeveloper}</strong>
                  <a href="mailto:xavier@xadevs.com">xavier@xadevs.com</a>
                </ContactItem>
              </ContactBox>
            </InfoSection>

            <InfoSection>
              <InfoSectionTitle>
                <FiUsers />
                {t.productsTitle}
              </InfoSectionTitle>
              <InfoText>
                <strong>VolpeTrack</strong><br />
                {t.volpetrackDesc}
              </InfoText>
              <InfoText style={{ marginBottom: 0 }}>
                <strong>HoldThen</strong><br />
                {t.holdthenDesc}
              </InfoText>
            </InfoSection>
          </SupportInfo>

          <SupportForm
            ref={formRef}
            initial="hidden"
            animate={formInView ? "visible" : "hidden"}
            variants={slideInRight}
          >
            <FormTitle>
              {t.formTitle}
              <ProductBadge product={formState.product}>
                {formState.product === 'volpetrack' ? 'VolpeTrack' : 'HoldThen'}
              </ProductBadge>
            </FormTitle>

            <Form onSubmit={handleSubmit}>
              <FormField>
                <Label htmlFor="product">{t.productLabel} *</Label>
                <Select
                  id="product"
                  name="product"
                  value={formState.product}
                  onChange={handleChange}
                >
                  <option value="volpetrack">VolpeTrack</option>
                  <option value="holdthen">HoldThen</option>
                </Select>
              </FormField>

              <FormRow>
                <FormField>
                  <Label htmlFor="name">{t.nameLabel} *</Label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={formState.name}
                    onChange={handleChange}
                    error={errors.name}
                    placeholder={language === 'en' ? 'John Doe' : 'Juan Pérez'}
                  />
                  {errors.name && (
                    <ErrorMessage>
                      <FiAlertCircle />
                      {errors.name}
                    </ErrorMessage>
                  )}
                </FormField>

                <FormField>
                  <Label htmlFor="email">{t.emailLabel} *</Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formState.email}
                    onChange={handleChange}
                    error={errors.email}
                    placeholder={language === 'en' ? 'john@example.com' : 'juan@ejemplo.com'}
                  />
                  {errors.email && (
                    <ErrorMessage>
                      <FiAlertCircle />
                      {errors.email}
                    </ErrorMessage>
                  )}
                </FormField>
              </FormRow>

              <FormRow>
                <FormField>
                  <Label htmlFor="issueType">{t.issueTypeLabel} *</Label>
                  <Select
                    id="issueType"
                    name="issueType"
                    value={formState.issueType}
                    onChange={handleChange}
                  >
                    <option value="general">{t.issueTypes.general}</option>
                    <option value="technical">{t.issueTypes.technical}</option>
                    <option value="account">{t.issueTypes.account}</option>
                    <option value="feature">{t.issueTypes.feature}</option>
                    <option value="bug">{t.issueTypes.bug}</option>
                    <option value="other">{t.issueTypes.other}</option>
                  </Select>
                </FormField>

                <FormField>
                  <Label htmlFor="subject">{t.subjectLabel} *</Label>
                  <Input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formState.subject}
                    onChange={handleChange}
                    error={errors.subject}
                    placeholder={language === 'en' ? 'Brief summary' : 'Resumen breve'}
                  />
                  {errors.subject && (
                    <ErrorMessage>
                      <FiAlertCircle />
                      {errors.subject}
                    </ErrorMessage>
                  )}
                </FormField>
              </FormRow>

              <FormField>
                <Label htmlFor="message">{t.messageLabel} *</Label>
                <TextArea
                  id="message"
                  name="message"
                  value={formState.message}
                  onChange={handleChange}
                  error={errors.message}
                  placeholder={language === 'en'
                    ? 'Please describe your issue in detail...'
                    : 'Por favor describe tu problema en detalle...'}
                />
                {errors.message && (
                  <ErrorMessage>
                    <FiAlertCircle />
                    {errors.message}
                  </ErrorMessage>
                )}
              </FormField>

              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? t.submitting : t.submitButton}
                {!isSubmitting && <FiSend />}
              </SubmitButton>

              {formMessage && (
                <FormMessage type={formMessage.type}>
                  {formMessage.type === 'success' ? <FiCheck /> : <FiAlertCircle />}
                  {formMessage.text}
                </FormMessage>
              )}
            </Form>
          </SupportForm>
          </SupportLayout>
        </ContentWrapper>
      </PageContainer>
    </>
  );
};

export default Support;
